import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ZodNumber, ZodString } from "zod";
import {
  annualDateTimeframeSchema,
  fullDateTimeframeSchema,
} from "../marketing/segments/segment-timeframe-zod-schema";
import {
  ComparisonType,
  ExistsCompareOperators,
  ListCompareOperators,
  NumericCompareOperator,
  ProximityCompareOperators,
  ProximityUnit,
  StringCompareOperators,
  TokenCompareOperators,
  WhereConditionDataType,
} from "./segment-where-condition";
const listCompareSchema = (zType: ZodNumber | ZodString) =>
  z.object({
    type: z.literal(ComparisonType.LIST),
    operator: createEnum(ListCompareOperators, {
      enumName: "ListCompareOperators",
    }),
    values: z.array(zType),
  });

const tokenCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    values: z.array(z.string()),
  })
  .meta({ id: "tokenCompare" });

export type TokenCondition = z.infer<typeof tokenConditionSchema>;
const tokenConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.TOKEN),
  dimension: z.string(),
  comparison: tokenCompareSchema,
});

const numericCompareSchema = z
  .object({
    type: z.literal(ComparisonType.NUMERIC),
    operator: createEnum(NumericCompareOperator, {
      enumName: "NumericCompareOperator",
    }),
    value: z.number(),
  })
  .meta({ id: "numericCompare" });

export type NumericCondition = z.infer<typeof numericConditionSchema>;
const numericConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.NUMERIC),
  dimension: z.string(),
  comparison: numericCompareSchema,
});

const booleanCompareSchema = z
  .object({ type: z.literal(ComparisonType.BOOLEAN), value: z.boolean() })
  .meta({ id: "booleanCompare" });

const existsCompareSchema = z
  .object({
    type: z.literal(ComparisonType.EXISTS),
    operator: createEnum(ExistsCompareOperators, {
      enumName: "ExistsCompareOperators",
    }),
  })
  .meta({ id: "existsCompare" });

export type BooleanCondition = z.infer<typeof booleanConditionSchema>;
const booleanConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.BOOLEAN),
  dimension: z.string(),
  comparison: booleanCompareSchema,
});

export type FullDateCondition = z.infer<typeof fullDateConditionSchema>;
const fullDateConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.DATE_FULL),
  dimension: z.string(),
  comparison: fullDateTimeframeSchema,
});

export type AnnualDateCondition = z.infer<typeof annualDateConditionSchema>;
const annualDateConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.DATE_ANNUAL),
  dimension: z.string(),
  // TODO (@javaroc): Once we have zod 4, remove this cast.
  // Right now, not casting results in the marketing RPC
  // being too big for typescript to handle.
  comparison: annualDateTimeframeSchema as z.ZodTypeAny,
});

export type TokenListCondition = z.infer<typeof tokenListConditionSchema>;
const tokenListConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.TOKEN_LIST),
  dimension: z.string(),
  comparison: listCompareSchema(z.string()),
});

const tokenHierarchyCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    prerequisiteValues: z.array(z.string()),
    values: z.array(z.string()),
  })
  .meta({ id: "tokenHierarchyCompare" });
export type TokenHierarchyCondition = z.infer<
  typeof tokenHierarchyConditionSchema
>;
export const tokenHierarchyConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.TOKEN_HIERARCHY),
  dimension: z.string(),
  comparison: tokenHierarchyCompareSchema,
});

const cityProximityCompareSchema = z
  .object({
    type: z.literal(ComparisonType.PROXIMITY),
    operator: createEnum(ProximityCompareOperators, {
      enumName: "ProximityCompareOperators",
    }),
    prerequisiteValues: z.array(z.string()), // [country, state]
    value: z.string(), // city
    options: z.object({
      radius: z.number(),
      units: createEnum(ProximityUnit, { enumName: "ProximityUnit" }),
    }),
  })
  .meta({ id: "cityProximityCompare" });

export type CityProximityCondition = z.infer<
  typeof cityProximityConditionSchema
>;
const cityProximityConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.CITY_PROXIMITY),
  dimension: z.string(),
  comparison: cityProximityCompareSchema,
});

const stringCompareSchema = z
  .object({
    type: z.literal(ComparisonType.STRING),
    operator: createEnum(StringCompareOperators, {
      enumName: "StringCompareOperators",
    }),
    value: z.string(),
  })
  .meta({ id: "stringCompare" });

export type StringCondition = z.infer<typeof stringConditionSchema>;
const stringConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.STRING),
  dimension: z.string(),
  comparison: stringCompareSchema,
});

const customFieldConditionSchema = z.object({
  type: z.literal(WhereConditionDataType.CUSTOM_FIELD),
  dimension: z.string(),
  declarationId: z.string(),
  comparison: z.union([
    tokenCompareSchema,
    numericCompareSchema,
    booleanCompareSchema,
    existsCompareSchema,
    fullDateTimeframeSchema,
  ]),
});
export type CustomFieldCondition = z.infer<typeof customFieldConditionSchema>;

export type WhereCondition = z.infer<typeof whereConditionSchema>;
export const whereConditionSchema = z.union([
  tokenConditionSchema,
  numericConditionSchema,
  booleanConditionSchema,
  fullDateConditionSchema,
  annualDateConditionSchema,
  tokenListConditionSchema,
  tokenHierarchyConditionSchema,
  cityProximityConditionSchema,
  customFieldConditionSchema,
  stringConditionSchema,
]);
